/**
 * Admin session policy (ISO 27001 A.8.5 / A.5.17 alignment).
 *
 * Enforces:
 *  - Idle timeout:    30 minutes without user input → forced sign-out.
 *  - Absolute timeout: 8 hours from first admin-area entry → forced sign-out.
 *  - Role revocation: if the admin role disappears mid-session → forced sign-out.
 *  - MFA (AAL2):
 *      * If the session has a verified factor but is currently AAL1, force a
 *        step-up challenge at /auth/mfa before any admin work continues.
 *      * If the user has no factor enrolled, allow a 30-day soft grace from
 *        their first admin entry. After that, redirect to /auth/mfa to enrol.
 *
 * Mounted inside /admin/* via <AdminRoute> so it only runs for admins.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getOrInitGraceStart, isGraceExpired } from "@/lib/mfa";

const IDLE_MS = 30 * 60 * 1000;        // 30 minutes
const ABSOLUTE_MS = 8 * 60 * 60 * 1000; // 8 hours
const ROLE_POLL_MS = 60 * 1000;         // re-check role every minute

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "visibilitychange",
];

export function AdminSessionGuard({ isStaff }: { isStaff?: boolean } = {}) {
  const { user, isAdmin: isAdminRole, signOut, refreshRole } = useAuth();
  // Volunteers with delegated permissions count as authorised staff.
  const isAdmin = isStaff ?? isAdminRole;
  const navigate = useNavigate();
  const location = useLocation();
  const lastActivity = useRef<number>(Date.now());
  const sessionStart = useRef<number>(Date.now());
  const mfaChecked = useRef<string | null>(null);

  // Force sign-out helper.
  const forceSignOut = (reason: "idle" | "absolute" | "revoked") => {
    const messages = {
      idle: "Signed out after 30 minutes of inactivity.",
      absolute: "Admin session expired after 8 hours. Please sign in again.",
      revoked: "Your admin access was changed. Please sign in again.",
    } as const;
    toast({ title: "Session ended", description: messages[reason] });
    void signOut().finally(() => navigate("/auth", { replace: true }));
  };

  // Track user activity to reset the idle clock.
  useEffect(() => {
    const onActivity = () => {
      lastActivity.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((ev) =>
      document.addEventListener(ev, onActivity, { passive: true })
    );
    return () => {
      ACTIVITY_EVENTS.forEach((ev) =>
        document.removeEventListener(ev, onActivity)
      );
    };
  }, []);

  // Periodic timeout + role-revocation check.
  useEffect(() => {
    if (!user) return;
    const interval = window.setInterval(async () => {
      const now = Date.now();
      if (now - sessionStart.current > ABSOLUTE_MS) {
        forceSignOut("absolute");
        return;
      }
      if (now - lastActivity.current > IDLE_MS) {
        forceSignOut("idle");
        return;
      }
      // Re-verify admin role; if revoked, kill the session.
      await refreshRole();
    }, ROLE_POLL_MS);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // React to role loss (also handles the case where refreshRole flips isAdmin
  // outside our poll, e.g. on tab focus from useAuth's own listener).
  useEffect(() => {
    if (user && !isAdmin) {
      forceSignOut("revoked");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // MFA enforcement. Runs once per session per user; the /auth/mfa page
  // handles its own redirect back once AAL2 is reached.
  useEffect(() => {
    if (!user || !isAdmin) return;
    if (mfaChecked.current === user.id) return;
    mfaChecked.current = user.id;

    (async () => {
      const { data: aal, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) return; // fail-open on transient errors; next mount retries.

      const current = aal?.currentLevel;
      const next = aal?.nextLevel;

      // Already stepped up — nothing to do.
      if (current === "aal2") return;

      // Has a verified factor (next=aal2) but session is aal1 → must step up. Hard block.
      if (current === "aal1" && next === "aal2") {
        navigate(`/auth/mfa?redirect=${encodeURIComponent(location.pathname + location.search)}`, {
          replace: true,
        });
        return;
      }

      // No factor enrolled at all (current=aal1, next=aal1). Apply 30-day grace.
      const graceStart = getOrInitGraceStart(user.id);
      if (isGraceExpired(graceStart)) {
        navigate(`/auth/mfa?redirect=${encodeURIComponent(location.pathname + location.search)}`, {
          replace: true,
        });
      }
      // Within grace → AdminMfaBanner shows the prompt, no redirect.
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAdmin]);

  return null;
}
